import React from 'react';
import { useUserInfo } from '@/provider/UserInfoProvider';
import useRoute from '@/ueshook/useRoute';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  UserCircle,
  Images,
  Invoice,
  Gift,
  Question,
  SignOut,
} from '@phosphor-icons/react';

interface UserMenuDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenUserInfo: () => void;
  onLogout: () => void;
}

const UserMenuDropdown: React.FC<UserMenuDropdownProps> = ({
  isOpen,
  onClose,
  onOpenUserInfo,
  onLogout,
}) => {
  const { userInfo, isLoadingUser } = useUserInfo();
  const { push } = useRoute();

  const handleUpgrade = () => {
    push('/pricing');
    onClose();
  };

  const handleMyProjects = () => {
    push('/my-projects');
    onClose();
  };

  const handlePlansAndPricing = () => {
    push('/pricing');
    onClose();
  };

  const handleInviteFriends = () => {
    push('/invite');
    onClose();
  };

  const handleGetHelp = () => {
    push('/get-help');
    onClose();
  };

  const handleAccount = () => {
    onOpenUserInfo();
    onClose();
  };

  const handleLogout = () => {
    onLogout();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className='absolute right-0 top-12 flex w-80 flex-col rounded-md bg-white p-4 text-left shadow-lg ring-1 ring-black ring-opacity-5'
      onClick={(e) => e.stopPropagation()}
      onMouseLeave={onClose}
    >
      {isLoadingUser ? (
        <p className='text-black'>Loading user info...</p>
      ) : userInfo ? (
        <>
          <div className='mb-4 rounded-lg border border-gray-200 p-4'>
            {(() => {
              const items = [];

              // 添加 credits remaining 如果大于0
              if (userInfo.plan.available_count > 0) {
                items.push({
                  id: 'credits',
                  value: userInfo.plan.available_count,
                  text: 'credits remaining',
                });
              }

              // 添加 AI Headshot Pro 如果大于0
              if (userInfo.plan.id_photo.pro_limit > 0) {
                items.push({
                  id: 'pro',
                  value: userInfo.plan.id_photo.pro_limit,
                  text: 'AI Headshot Pro',
                });
              }

              // 添加 AI Headshot Basic 如果大于0
              if (userInfo.plan.id_photo.basic_limit > 0) {
                items.push({
                  id: 'basic',
                  value: userInfo.plan.id_photo.basic_limit,
                  text: 'AI Headshot Basic',
                });
              }

              // 如果没有任何项目，显示默认信息
              if (items.length === 0) {
                return (
                  <div className='text-center text-gray-500'>
                    No credits available
                  </div>
                );
              }

              // 渲染项目列表，在项目之间添加分隔线
              return items.map((item, index) => (
                <React.Fragment key={item.id}>
                  <div className='flex gap-1'>
                    <span className='text-xl font-bold text-[#6137E0]'>
                      {item.value}
                    </span>
                    <span className='text-lg font-medium text-gray-800'>
                      {item.text}
                    </span>
                  </div>
                  {index < items.length - 1 && <Separator className='my-2' />}
                </React.Fragment>
              ));
            })()}
          </div>

          <Button
            className='mb-4 w-full bg-[#6137E0] text-white hover:bg-[#6c45e0]'
            onClick={handleUpgrade}
          >
            Upgrade
          </Button>

          <div className='space-y-1'>
            <Button
              variant='ghost'
              className='w-full justify-start gap-3 px-0 py-2 text-left text-lg font-medium'
              onClick={handleAccount}
            >
              <UserCircle size={24} />
              Account
            </Button>

            <Button
              variant='ghost'
              className='w-full justify-start gap-3 px-0 py-2 text-left text-lg font-medium'
              onClick={handleMyProjects}
            >
              <Images size={24} />
              My projects
            </Button>

            <Button
              variant='ghost'
              className='w-full justify-start gap-3 px-0 py-2 text-left text-lg font-medium'
              onClick={handlePlansAndPricing}
            >
              <Invoice size={24} />
              Plans and pricing
            </Button>

            <Button
              variant='ghost'
              className='w-full justify-start gap-3 px-0 py-2 text-left text-lg font-medium'
              onClick={handleInviteFriends}
            >
              <Gift size={24} />
              Invite friends, earn credits
            </Button>

            <Button
              variant='ghost'
              className='w-full justify-start gap-3 px-0 py-2 text-left text-lg font-medium'
              onClick={handleGetHelp}
            >
              <Question size={24} />
              Get help
            </Button>

            <Button
              variant='ghost'
              className='w-full justify-start gap-3 px-0 py-2 text-left text-lg font-medium'
              onClick={handleLogout}
            >
              <SignOut size={24} className='' />
              Log out
            </Button>
          </div>
        </>
      ) : (
        <p className='text-black'>Loading user info...</p>
      )}
    </div>
  );
};

export default UserMenuDropdown;
